/**
 * UranIA PA — persistenza sessione onboarding R&I reload-safe (US-016 AC#3).
 *
 * AC US-016 letterale: "Progress persistence reload-safe".
 *
 * Implementazione speculare a `pa-session-storage.ts` (US-015):
 *   - Source of truth: backend (la session ha `current_step` lato Postgres,
 *     migration 0002, US-012). Allo (re)open la SPA chiama
 *     `createOnboardingRiSession` (idempotente per UNIQUE
 *     (tenant_id, user_id, profile_type) — US-014 AC#1) e riceve
 *     `current_step` ufficiale dal server.
 *   - Cache di transito: `sessionStorage` (NON `localStorage`) custodisce
 *     `session_id` + `current_step` indicizzato per `subject` (Keycloak
 *     `sub`). Cosi' un reload di tab esistente riprende la wizard al punto
 *     giusto SENZA dover ri-postare al server in race. Chiave separata
 *     rispetto al PA (per evitare collisione quando uno stesso operatore
 *     ha entrambi i profili — uso minore ma possibile, vedi CLAUDE.md
 *     "Onboarding flows differ by role").
 *   - Niente PII nel storage: solo ULID session_id + numero step + subject.
 *
 * Boundary di sicurezza (analisi §18): sessionStorage e' XSS-vulnerable
 * come ogni storage client-side. Per ridurre l'impatto, qui custodiamo
 * solo identificativi opachi (session ULID, step int); le rotte
 * `/steps/{n}` richiedono comunque step-up MFA RFC 9470 (US-014).
 */

import type { OnboardingRiSessionDto } from './ri-schemas.ts';

const STORAGE_KEY = 'urania.onboarding.ri.session.v1';

export interface OnboardingRiSnapshot {
  readonly subject: string;
  readonly sessionId: string;
  readonly currentStep: number;
  readonly completed: boolean;
}

function safeStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function saveOnboardingRiSnapshot(snapshot: OnboardingRiSnapshot): void {
  const storage = safeStorage();
  if (storage === null) {
    return;
  }
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // QuotaExceededError, SecurityError in iframe sandboxati: best-effort.
  }
}

export function loadOnboardingRiSnapshot(subject: string): OnboardingRiSnapshot | null {
  if (subject.length === 0) {
    return null;
  }
  const storage = safeStorage();
  if (storage === null) {
    return null;
  }
  let raw: string | null;
  try {
    raw = storage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<OnboardingRiSnapshot>;
    if (
      typeof parsed.subject !== 'string' ||
      typeof parsed.sessionId !== 'string' ||
      typeof parsed.currentStep !== 'number' ||
      typeof parsed.completed !== 'boolean'
    ) {
      return null;
    }
    if (parsed.subject !== subject) {
      return null;
    }
    return {
      subject: parsed.subject,
      sessionId: parsed.sessionId,
      currentStep: parsed.currentStep,
      completed: parsed.completed,
    };
  } catch {
    return null;
  }
}

export function clearOnboardingRiSnapshot(): void {
  const storage = safeStorage();
  if (storage === null) {
    return;
  }
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // best-effort.
  }
}

export function snapshotFromSessionDto(
  subject: string,
  session: OnboardingRiSessionDto,
): OnboardingRiSnapshot {
  return {
    subject,
    sessionId: session.session_id,
    currentStep: session.current_step,
    completed: typeof session.completed_at === 'string' && session.completed_at.length > 0,
  };
}
